// File: src/components/pages/Report.jsx
// Lesson Payment Report — shows all lessons with payment status for the instructor

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  FileText, RefreshCw, AlertCircle, Download,
  CheckCircle2, Clock, CalendarDays, Search
} from 'lucide-react';
import { reportAPI } from '../../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
const today   = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };

const StatusBadge = ({ status }) => {
  const collected = status === 'Collected';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.2rem 0.65rem', borderRadius: '999px',
      fontSize: '0.75rem', fontWeight: 700,
      background: collected ? '#d1fae5' : '#fef3c7',
      color:      collected ? '#065f46' : '#92400e',
      whiteSpace: 'nowrap'
    }}>
      {collected ? <CheckCircle2 size={11} /> : <Clock size={11} />}
      {status}
    </span>
  );
};

const SummaryCard = ({ label, value, color, icon: Icon }) => (
  <div style={{
    flex: 1, minWidth: '130px',
    background: '#fff', borderRadius: '0.75rem',
    border: `1.5px solid ${color}33`,
    padding: '1rem 1.25rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
      <Icon size={16} color={color} />
      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 800, color }}>
      {value}
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
const Report = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [rows,      setRows]      = useState([]);
  const [meta,      setMeta]      = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState(null);
  const [fromDate,  setFromDate]  = useState(daysAgo(30));
  const [toDate,    setToDate]    = useState(today());
  const [search,    setSearch]    = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await reportAPI.getLessonPaymentReport(
        { from: fromDate, to: toDate },
        getAccessTokenSilently
      );
      if (res.success) {
        setRows(res.data  || []);
        setMeta(res.meta  || null);
      } else {
        setError(res.error || 'Failed to load report.');
      }
    } catch (err) {
      setError(err.message || 'Failed to load report.');
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, getAccessTokenSilently]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // ── Client-side filter ────────────────────────────────────────────────────
  const filtered = rows.filter(r => {
    const matchSearch = !search ||
      r.studentName?.toLowerCase().includes(search.toLowerCase()) ||
      r.dateFormatted?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || r.paymentStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Date', 'Student Name', 'Start Time', 'End Time',
                     'Payment Status', 'Payment Date', 'Amount'];
    const rowsCSV = filtered.map(r => [
      r.dateFormatted, r.studentName, r.lessonFrom, r.lessonTo,
      r.paymentStatus, r.paymentDate || '—', r.amount ? `£${r.amount}` : '—'
    ]);
    const csv = [headers, ...rowsCSV].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a    = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `LessonPaymentReport-${fromDate}-to-${toDate}.csv`
    });
    a.click();
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const inputStyle = {
    padding: '0.45rem 0.75rem', fontSize: '0.875rem',
    border: '1.5px solid #e5e7eb', borderRadius: '0.5rem',
    fontFamily: 'inherit', outline: 'none', background: '#fff', color: '#111827'
  };
  const thStyle = {
    padding: '0.7rem 1rem', textAlign: 'left',
    fontSize: '0.72rem', fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap'
  };
  const tdStyle = {
    padding: '0.75rem 1rem', fontSize: '0.875rem',
    color: '#111827', borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'middle'
  };

  return (
    <div className="page-container" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <FileText size={28} color="#6366f1" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
              Lesson Payment Report
            </h1>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
              Showing lessons and payment status within the selected date range
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={loadReport} disabled={isLoading} style={{
            display: 'flex', alignItems: 'center', gap: '0.35rem',
            padding: '0.45rem 0.9rem', borderRadius: '0.5rem',
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#374151', fontSize: '0.85rem', fontWeight: 500,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1, fontFamily: 'inherit'
          }}>
            <RefreshCw size={14} style={isLoading ? { animation: 'spin 1s linear infinite' } : {}} />
            Refresh
          </button>
          {rows.length > 0 && (
            <button onClick={exportCSV} style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.45rem 0.9rem', borderRadius: '0.5rem',
              border: 'none',
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: '#fff', fontSize: '0.85rem', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 2px 6px rgba(99,102,241,0.3)'
            }}>
              <Download size={14} /> Export CSV
            </button>
          )}
        </div>
      </div>

      {/* ── Filters row ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        flexWrap: 'wrap', marginBottom: '1.25rem',
        padding: '0.875rem 1rem', background: '#f9fafb',
        borderRadius: '0.75rem', border: '1.5px solid #e5e7eb'
      }}>
        <CalendarDays size={16} color="#6366f1" style={{ flexShrink: 0 }} />

        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>From</label>
        <input type="date" value={fromDate} max={toDate}
          onChange={e => setFromDate(e.target.value)} style={inputStyle} />

        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>To</label>
        <input type="date" value={toDate} min={fromDate} max={today()}
          onChange={e => setToDate(e.target.value)} style={inputStyle} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
          flex: 1, minWidth: '180px',
          border: '1.5px solid #e5e7eb', borderRadius: '0.5rem',
          background: '#fff', padding: '0 0.65rem' }}>
          <Search size={14} color="#9ca3af" />
          <input type="text" placeholder="Search student or date…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, border: 'none', padding: '0.45rem 0', flex: 1, outline: 'none' }} />
        </div>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="All">All statuses</option>
          <option value="Collected">Collected</option>
          <option value="Pending">Pending</option>
        </select>
      </div>

      {/* ── Summary cards ─────────────────────────────────────────────────── */}
      {meta && !isLoading && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <SummaryCard label="Total Lessons"  value={meta.total}     color="#6366f1" icon={FileText} />
          <SummaryCard label="Collected"      value={meta.collected} color="#10b981" icon={CheckCircle2} />
          <SummaryCard label="Pending"        value={meta.pending}   color="#f59e0b" icon={Clock} />
          <SummaryCard label="Showing"        value={filtered.length}color="#6b7280" icon={CalendarDays} />
        </div>
      )}

      {/* ── States ───────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="loading-state"><div className="spinner" /><p>Loading report…</p></div>
      )}

      {!isLoading && error && (
        <div className="error-banner">
          <AlertCircle size={16} /><span>{error}</span>
          <button onClick={loadReport} style={{
            marginLeft: 'auto', background: '#dc2626', color: '#fff',
            border: 'none', borderRadius: '0.4rem',
            padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600
          }}>Retry</button>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="sp-entry-empty">
          <FileText size={32} style={{ color: '#d1d5db' }} />
          <p>No lessons found for the selected date range and filters.</p>
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {!isLoading && !error && filtered.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: '0.75rem',
          border: '1.5px solid #e5e7eb',
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  {['Date', 'Student Name', 'Start Time', 'End Time',
                    'Payment Status', 'Payment Date', 'Amount'].map(col => (
                    <th key={col} style={thStyle}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={`${row.bookingId || i}`}
                    style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                  >
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 600, color: '#374151' }}>
                        {row.dateFormatted || row.date}
                      </span>
                    </td>
                    <td style={tdStyle}>{row.studentName || '—'}</td>
                    <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                      {row.lessonFrom || row.startTime || '—'}
                    </td>
                    <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                      {row.lessonTo   || row.endTime   || '—'}
                    </td>
                    <td style={tdStyle}><StatusBadge status={row.paymentStatus} /></td>
                    <td style={{ ...tdStyle, color: row.paymentDate ? '#111827' : '#9ca3af' }}>
                      {row.paymentDate || '—'}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600,
                      color: row.amount ? '#111827' : '#9ca3af' }}>
                      {row.amount ? `£${Number(row.amount).toFixed(2)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          <div style={{
            padding: '0.6rem 1rem', borderTop: '1px solid #f3f4f6',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '0.8rem', color: '#6b7280'
          }}>
            <span>
              Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong> record{rows.length !== 1 ? 's' : ''}
            </span>
            {meta && (
              <span>
                {meta.from} → {meta.to}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Report;